/**
 * Centralized number formatting utilities.
 * Uses native Intl.NumberFormat — no external dependencies.
 */

const intFormatter = new Intl.NumberFormat("en-US");

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats an integer with thousands separators.
 * @example formatInt(1234567) → "1,234,567"
 */
export function formatInt(value: number): string {
  return intFormatter.format(value);
}

/**
 * Formats a dollar amount (floating-point) with $ symbol and 2 decimal places.
 * @example formatCurrency(1234.5) → "$1,234.50"
 */
export function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

/**
 * Formats an integer cent amount as a USD currency string.
 * @example formatCents(123450) → "$1,234.50"
 */
export function formatCents(cents: number): string {
  return formatCurrency(cents / 100);
}

/**
 * Formats a points balance with "pts" suffix.
 * @example formatPoints(1500) → "1,500 pts"
 */
export function formatPoints(points: number): string {
  return `${intFormatter.format(points)} pts`;
}

/**
 * Parses a dollar string to cents.
 * @example parseCents("9.99") → 999
 */
export function parseCents(dollars: string): number {
  const val = parseFloat(dollars);
  if (isNaN(val)) return 0;
  return Math.round(val * 100);
}

/**
 * Formats an integer with an explicit +/- sign.
 * @example formatSigned(12) → "+12", formatSigned(-5) → "-5"
 */
export function formatSigned(value: number): string {
  return value > 0 ? `+${formatInt(value)}` : formatInt(value);
}
